/**
 * A workbench Word draft — or an earlier version of the report — opened from
 * its file. A draft's cited paragraphs are tinted, the records each rests on
 * as chips at its end (by their numbers; a chip opens its record beside the
 * view). The reviewer's issues sit beside it — on a wide screen in a margin,
 * each card level with its paragraph (`ReviewMargin`), else as a list — and
 * a paragraph with an open issue carries a mark: click the paragraph for its
 * issue, or an issue for its paragraph.
 *
 * Worked from Needs you (WS-22): opened at an issue (`issue`), its card
 * selected; `j` and `k` move between the draft's open issues in Needs you's
 * order, `1` fixes the selected one (a layout fault's one-step fix, else its
 * paragraph opens for editing — ⌘↵ saves the new draft and calls the issue
 * fixed), `2` keeps it, `3` dismisses it. A strip stays in view with the
 * issues open and the way back to Needs you; an issue with a page shows on
 * the pages as the client gets them.
 *
 * The latest draft is edited before it's released: its text in place, as the
 * report as it stands is, and a layout fault's one-step fix.
 * The changes wait in `Workbench` (kept across views) and save together as
 * one new draft, which takes the issues over; "Fixed" is offered once a
 * person has changed an issue's paragraph. Release makes the draft the
 * report's next version — the first release sends the report to QA where
 * approval is required.
 *
 * A live draft the case has moved on from (`outdated`) says so: the
 * workbench doesn't redraft over a person's edits — a redraft is asked for.
 * A paragraph a link names scrolls into view and flashes; selected text
 * offers itself to the copilot.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent } from 'react';

import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Grid,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { useElementSize } from '@mantine/hooks';

import * as Sentry from '@sentry/react';

import {
  DocxContent,
  editPayload,
  isEditableBlock,
  paragraphTarget,
  recheckEdits,
  type DocxEditBinding,
  type PendingEdits,
} from '@/components/Containers/DocxContent';
import { useTextSelection } from '@/hooks/useTextSelection';
import { requestAnchorHighlight, setPersistentAnchorHighlight } from '@/lib/anchorHighlight';
import { useCopilotMarks } from '@/lib/copilotHighlight';
import type { BlockModel, DocxModel } from '@/lib/ooxml';
import { ApiConflictError, vnextApiClient } from '@/services/vnext-api';
import type { CopilotPassage } from '@/types/copilot';
import type {
  DraftCitations,
  DraftEdited,
  DraftRelease,
  ReviewIssue,
  WorkbenchDocument,
  WorkbenchHeader,
} from '@/types/workbench';
import { toError } from '@/utils/errors';
import { DocumentIssueStrip } from './DocumentIssueStrip';
import { words } from './format';
import { CONTAINER_BREAKPOINTS, MARGIN_WIDTH } from './layout';
import { LayoutCheckPanel } from './LayoutCheckPanel';
import { anchorElement, editKey, paraKey } from './marginLayout';
import { issueOrder } from './needsYouOrder';
import { PagesModal } from './PagesModal';
import { RecordChips } from './RecordChips';
import { IssueCard, ReviewIssues, type Resolution } from './ReviewIssues';
import { ReviewMargin, type MarginPlace } from './ReviewMargin';
import { useWorkbenchKeys } from './useWorkbenchKeys';
import classes from './DraftView.module.css';

/** A draft's changes not saved yet: its text edits, and the layout issues
 * whose one-step fix to make. */
export interface DraftChanges {
  draftId: string;
  edits: PendingEdits;
  fixes: ReadonlySet<string>;
}

const NO_EDITS: PendingEdits = new Map();
const NO_FIXES: ReadonlySet<string> = new Set();

interface DraftViewProps {
  header: WorkbenchHeader;
  document: WorkbenchDocument;
  /** The reviewer's issues on this draft. */
  issues: ReviewIssue[];
  /** After a release, a save or a closed issue (the case refetches). */
  onChanged: () => void | Promise<void>;
  /** The draft was released (the view shows the report, and says so). */
  onReleased: (notice: string) => void;
  /** The changes not saved yet (kept by the workbench across views). */
  changes: DraftChanges | null;
  onChangesChange: (changes: DraftChanges | null) => void;
  /** The changes were saved as a new draft (the view opens it, and says so). */
  onSaved: (draftId: string, notice: string) => void;
  /** A paragraph a link names: scrolled to and flashed. */
  focusedPara?: string | null;
  /** A passage selected in the document, offered to the copilot. */
  onAskCopilot?: (passage: CopilotPassage) => void;
  /** The issue in focus (a link's, or the one selected here). */
  focusedIssue?: string | null;
  /** An issue selected here: the link follows. */
  onFocusIssue?: (issueId: string | null) => void;
  /** What Needs you holds, for the way back to it. */
  needsYouTotal?: number;
  /** Back to Needs you, at an item (an issue of this draft's), or its top. */
  onBackToNeedsYou?: (issueId: string | null) => void;
  /** Issues a person closed here: Needs you doesn't count them as settled
   * by an agent. */
  onClosedHere?: (issueIds: string[]) => void;
}

const BACKOFFICE: Record<DraftRelease['backoffice'], string> = {
  done: 'its Backoffice project is completed',
  waits_for_approval: "it's in QA — Backoffice completes on approval",
  no_project: 'it has no Backoffice project',
  failed: "Backoffice wasn't reached — release again to retry",
};

function released(result: DraftRelease): string {
  if (result.repeated) {
    return `Already released as version ${result.version}.`;
  }
  return `Released as version ${result.version}: ${BACKOFFICE[result.backoffice]}.`;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

function savedNotice(saved: DraftEdited): string {
  const parts = [];
  if (saved.edits) {
    parts.push(plural(saved.edits, 'text change'));
  }
  if (saved.fixed.length) {
    parts.push(plural(saved.fixed.length, 'layout fix', 'layout fixes'));
  }
  return `Saved as a new draft${parts.length ? `: ${parts.join(' and ')}` : ''}.`;
}

export function DraftView({
  header,
  document,
  issues,
  onChanged,
  onReleased,
  changes,
  onChangesChange,
  onSaved,
  focusedPara = null,
  onAskCopilot,
  focusedIssue = null,
  onFocusIssue,
  needsYouTotal = 0,
  onBackToNeedsYou,
  onClosedHere,
}: Readonly<DraftViewProps>) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [citations, setCitations] = useState<DraftCitations>({});
  const [localIssue, setLocalIssue] = useState<string | null>(null);
  // The issue being fixed by hand: ⌘↵ saves the draft, then calls it fixed.
  const [fixing, setFixing] = useState<string | null>(null);
  // The last issue a person closed here: the way back to Needs you goes on
  // from it.
  const [lastClosed, setLastClosed] = useState<string | null>(null);
  const [pagesAt, setPagesAt] = useState<{ page: number; issueId: string | null } | null>(null);
  // ⌘↵ asked for a save: made once the editor's last keystrokes are in.
  const [saveAsked, setSaveAsked] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Issues closed here whose close is still on its way to the API: out of
  // the list at once (decision 19), back in with a notice if it fails.
  const [closing, setClosing] = useState<ReadonlySet<string>>(new Set());
  // One case refetch for a run of closes, not one each.
  const refetchTimer = useRef<number | null>(null);
  const scheduleRefetch = () => {
    if (refetchTimer.current !== null) {
      window.clearTimeout(refetchTimer.current);
    }
    refetchTimer.current = window.setTimeout(() => {
      refetchTimer.current = null;
      void onChanged();
    }, 600);
  };
  useEffect(
    () => () => {
      if (refetchTimer.current !== null) {
        window.clearTimeout(refetchTimer.current);
      }
    },
    []
  );
  const [releasing, setReleasing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ color: string; text: string } | null>(null);
  // Bumped when the draft's blocks are on the page, so the marks follow.
  const [rendered, setRendered] = useState(0);
  const draft = document.kind === 'draft';
  const mine = changes?.draftId === document.id ? changes : null;
  const edits = mine?.edits ?? NO_EDITS;
  const fixes = mine?.fixes ?? NO_FIXES;
  const pending = edits.size + fixes.size;
  const [editing, setEditing] = useState(edits.size > 0);
  // The margin needs the width the view has, whatever the screen's.
  const { ref: sizeRef, width } = useElementSize();
  const margin = width >= MARGIN_WIDTH && draft;
  const docRef = useRef<HTMLDivElement>(null);
  const cards = useRef(new Map<string, HTMLDivElement>());
  // The draft as read: where an issue's paragraph is, to open it for editing.
  const modelRef = useRef<DocxModel | null>(null);
  const selectedIssue = onFocusIssue ? focusedIssue : (localIssue ?? focusedIssue);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setFailed(null);
    setCitations({});
    setLocalIssue(null);
    setFixing(null);
    (async () => {
      try {
        const [file, cited] = await Promise.all([
          vnextApiClient.getWorkbenchDocumentFile(header.reportId, document.kind, document.id),
          draft
            ? vnextApiClient.getDraftCitations(header.reportId, document.id)
            : Promise.resolve({}),
        ]);
        if (!cancelled) {
          setUrl(file.url);
          setCitations(cited);
        }
      } catch (err) {
        Sentry.captureException(toError(err));
        if (!cancelled) {
          setFailed(toError(err, "The document couldn't be opened").message);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [header.reportId, document.kind, document.id, draft]);

  // A paragraph a link names, once the draft's blocks are on the page.
  useEffect(() => {
    if (focusedPara && rendered > 0) {
      requestAnchorHighlight([focusedPara]);
    }
  }, [focusedPara, rendered]);

  useTextSelection(
    docRef,
    (text, paraIds) =>
      onAskCopilot?.({
        document: draft ? 'draft' : 'version',
        documentId: document.id,
        paraIds: paraIds.slice(0, 40),
        quote: text.slice(0, 2000),
      }),
    { enabled: Boolean(onAskCopilot) && !editing, label: 'Ask the Assistant' }
  );
  useCopilotMarks(docRef);

  // Tint the paragraphs that cite evidence while the draft is shown.
  const citedKey = Object.keys(citations).join('|');
  useEffect(() => {
    setPersistentAnchorHighlight(citedKey ? citedKey.split('|') : []);
    return () => setPersistentAnchorHighlight([]);
  }, [citedKey]);

  // The draft's open issues in the order they're worked (Needs you's).
  const open = useMemo(
    () => issues.filter((i) => i.status === 'open' && !closing.has(i.id)).sort(issueOrder),
    [issues, closing]
  );
  // The cards: an issue whose close is on its way is out of them already.
  const shown = useMemo(() => issues.filter((i) => !closing.has(i.id)), [issues, closing]);
  // The open issues by rule, the most first — the strip's bulk actions.
  const groups = useMemo(() => {
    const counts = new Map<string, number>();
    for (const issue of open) {
      counts.set(issue.rule, (counts.get(issue.rule) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([rule, count]) => ({ rule, count }))
      .sort((a, b) => b.count - a.count || a.rule.localeCompare(b.rule));
  }, [open]);
  const changed = useMemo(() => new Set(document.changed), [document.changed]);
  const pendingParas = useMemo(() => new Set([...edits.values()].map(editKey)), [edits]);

  // Each open issue's paragraph marked, the selected one's outlined — again
  // after every render, as a re-rendered block loses classes set from here.
  useLayoutEffect(() => {
    const root = docRef.current;
    if (!root) {
      return undefined;
    }
    const applied: [HTMLElement, string][] = [];
    const mark = (el: HTMLElement | null, name: string | undefined) => {
      if (el && name) {
        el.classList.add(name);
        applied.push([el, name]);
      }
    };
    for (const issue of open) {
      mark(
        anchorElement(root, issue),
        issue.severity === 'error' ? classes.markError : classes.markWarning
      );
    }
    const chosen = issues.find((i) => i.id === selectedIssue);
    if (chosen) {
      mark(anchorElement(root, chosen), classes.anchorSelected);
    }
    return () => applied.forEach(([el, name]) => el.classList.remove(name));
  });

  const canEdit = draft && header.mode === 'live' && document.status === 'built';

  // A paragraph's citations as chips at its end (a table's after it).
  const decorate = useMemo(() => {
    if (!draft || Object.keys(citations).length === 0) {
      return undefined;
    }
    const byPara = new Map(
      Object.entries(citations).map(([paraId, ids]) => [paraId.toUpperCase(), ids])
    );
    return (block: BlockModel) => {
      const ids = block.paraId ? byPara.get(block.paraId.toUpperCase()) : undefined;
      return ids?.length ? <RecordChips ids={ids} /> : null;
    };
  }, [draft, citations]);

  const setChanges = (next: { edits?: PendingEdits; fixes?: ReadonlySet<string> }) => {
    const nextEdits = next.edits ?? edits;
    const nextFixes = next.fixes ?? fixes;
    onChangesChange(
      nextEdits.size + nextFixes.size > 0
        ? { draftId: document.id, edits: nextEdits, fixes: nextFixes }
        : null
    );
  };

  /** An issue picked: the link follows (or this view keeps it). */
  const select = (issueId: string | null) => {
    if (onFocusIssue) {
      onFocusIssue(issueId);
    } else {
      setLocalIssue(issueId);
    }
  };

  // The issue in focus — a link's, a key's, a click's: its paragraph
  // scrolled to, flashed and outlined, its card brought into view.
  useEffect(() => {
    const issue = issues.find((i) => i.id === selectedIssue);
    if (!issue || rendered === 0) {
      return;
    }
    const el = docRef.current ? anchorElement(docRef.current, issue) : null;
    if (el) {
      el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      el.classList.add(classes.flash);
      window.setTimeout(() => el.classList.remove(classes.flash), 2600);
    }
    cards.current.get(issue.id)?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [selectedIssue, rendered > 0]);

  /** A paragraph clicked: its issue. */
  const onDocClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const block = target.closest<HTMLElement>('[data-para-id], [data-pos]');
    if (!block) {
      return;
    }
    const paraId = block.dataset.paraId;
    const key = paraId ? paraId.toUpperCase() : `pos:${block.dataset.pos}`;
    const row = target.closest<HTMLElement>('td[data-row]')?.dataset.row;
    const here = open.find(
      (issue) =>
        paraKey(issue) === key &&
        (issue.row === null || row === undefined || String(issue.row) === row)
    );
    if (here) {
      select(here.id);
    }
  };

  /** Close an issue as a person decides: it leaves the list and the next one
   * down takes the focus at once; the call goes behind (a failure puts it
   * back, with why); the case refetches once for a run of them. */
  const resolveIssue = async (issue: ReviewIssue, status: Resolution) => {
    const at = open.findIndex((i) => i.id === issue.id);
    const next = open.slice(at + 1).find((i) => i.id !== issue.id) ?? null;
    setClosing((prev) => new Set([...prev, issue.id]));
    setLastClosed(issue.id);
    if (fixing === issue.id) {
      setFixing(null);
    }
    if (selectedIssue === issue.id) {
      select(next?.id ?? null);
    }
    try {
      await vnextApiClient.resolveReviewIssue(header.reportId, issue.id, status);
    } catch (err) {
      setClosing((prev) => {
        const back = new Set(prev);
        back.delete(issue.id);
        return back;
      });
      setNotice({
        color: 'red',
        text: `Not recorded: ${toError(err, 'the issue stays open').message}`,
      });
      throw err;
    }
    onClosedHere?.([issue.id]);
    scheduleRefetch();
  };

  /** Every open issue of a rule — or every one left — closed in one go. */
  const resolveAll = async (
    status: 'accepted' | 'dismissed',
    rule: string | null,
    note: string | null
  ) => {
    const ids = open.filter((i) => rule === null || i.rule === rule).map((i) => i.id);
    setClosing((prev) => new Set([...prev, ...ids]));
    if (selectedIssue && ids.includes(selectedIssue)) {
      select(open.find((i) => !ids.includes(i.id))?.id ?? null);
    }
    try {
      const done = await vnextApiClient.resolveReviewIssues(header.reportId, {
        status,
        rule,
        note,
      });
      onClosedHere?.(done.reviewIssueIds);
    } catch (err) {
      setClosing((prev) => {
        const back = new Set(prev);
        ids.forEach((id) => back.delete(id));
        return back;
      });
      setNotice({
        color: 'red',
        text: `Not recorded: ${toError(err, 'the issues stay open').message}`,
      });
      throw err;
    }
    await onChanged();
  };

  /** The block an issue's paragraph is, where the draft has one to edit. */
  const editableBlock = (issue: ReviewIssue): BlockModel | null => {
    const model = modelRef.current;
    const key = paraKey(issue);
    if (!model || !key || issue.row !== null) {
      return null;
    }
    const block = model.blocks.find((b) =>
      key.startsWith('pos:') ? `pos:${b.pos}` === key : b.paraId?.toUpperCase() === key
    );
    return block && isEditableBlock(block) ? block : null;
  };

  /** Fix it yourself: its paragraph opens for editing — or, where this view
   * can't open it (a table cell, a paragraph it doesn't edit), says so and
   * what to do instead. */
  const fixYourself = (issue: ReviewIssue) => {
    const block = editableBlock(issue);
    if (!block) {
      setNotice({
        color: 'blue',
        text:
          issue.row !== null
            ? 'This issue sits in a table cell, which this view does not edit yet: fix it in Word or ask the Assistant, then press 4 to call it fixed.'
            : 'This issue is not on a paragraph this view can edit: fix it in Word or ask the Assistant, then press 4 to call it fixed.',
      });
      return;
    }
    select(issue.id);
    setFixing(issue.id);
    setEditing(true);
    setActive(paragraphTarget(block).key);
  };

  const toggleFix = (chosen: ReviewIssue) => {
    const next = new Set(fixes);
    if (next.has(chosen.id)) {
      next.delete(chosen.id);
    } else {
      next.add(chosen.id);
    }
    setChanges({ fixes: next });
  };

  const binding: DocxEditBinding = {
    pending: edits,
    active,
    onActivate: (target) => {
      if (!saving) {
        setActive(target.key);
      }
    },
    onChange: (target, text) => {
      const next = new Map(edits);
      if (text === target.expected) {
        next.delete(target.key);
      } else {
        next.set(target.key, { target, text });
      }
      setChanges({ edits: next });
    },
    onClose: () => setActive(null),
    // Changes made on a draft a newer one replaced: kept where its
    // paragraphs still read as they did.
    onLoad: (model) => {
      if (edits.size === 0) {
        return;
      }
      const { kept, dropped } = recheckEdits(edits, model);
      setChanges({ edits: kept });
      if (dropped.length) {
        setNotice({
          color: 'orange',
          text: `${plural(dropped.length, 'of your changes', 'of your changes')} no longer ${
            dropped.length === 1 ? 'fits' : 'fit'
          } this draft and ${dropped.length === 1 ? 'was' : 'were'} dropped.`,
        });
      }
    },
  };

  const save = async () => {
    const list = [...edits.values()];
    // The issue fixed by hand, if its paragraph is among the changes: called
    // fixed once the new draft holds them (the issues move with the draft).
    const fixed = open.find((i) => i.id === fixing);
    const fixedKey = fixed ? paraKey(fixed) : null;
    const callFixed = fixed && fixedKey && list.some((edit) => editKey(edit) === fixedKey);
    setSaving(true);
    setActive(null);
    setNotice(null);
    try {
      const saved = await vnextApiClient.saveDraftEdits(
        header.reportId,
        document.id,
        editPayload(list),
        [...fixes]
      );
      onChangesChange(null);
      setEditing(false);
      setFixing(null);
      const closed = new Set(saved.fixed);
      if (callFixed && fixed) {
        try {
          await vnextApiClient.resolveReviewIssue(header.reportId, fixed.id, 'fixed');
          closed.add(fixed.id);
          setLastClosed(fixed.id);
        } catch (err) {
          Sentry.captureException(toError(err));
        }
      }
      onClosedHere?.([...closed]);
      // A fix closes its issue as keeping or dismissing does: the next one
      // down takes the focus.
      if (selectedIssue && closed.has(selectedIssue)) {
        const at = open.findIndex((i) => i.id === selectedIssue);
        select(open.slice(at + 1).find((i) => !closed.has(i.id))?.id ?? null);
      }
      await onChanged();
      onSaved(saved.draftId, savedNotice(saved));
    } catch (err) {
      if (err instanceof ApiConflictError && err.detail.reason === 'mismatch') {
        // Failures index the edits as sent; keep the rest.
        const failures = (err.detail.failures ?? []) as { index: number; reason: string }[];
        const failed = new Set(failures.map((f) => f.index));
        const rest = new Map(
          list.filter((_, i) => !failed.has(i)).map((edit) => [edit.target.key, edit])
        );
        setChanges({ edits: rest });
        setNotice({
          color: 'orange',
          text: `${plural(failed.size, 'change')} couldn't be made and ${
            failed.size === 1 ? 'was' : 'were'
          } dropped (${failures.map((f) => f.reason).join('; ')}).${
            rest.size + fixes.size ? ' The rest are kept — save again.' : ''
          }`,
        });
      } else if (err instanceof ApiConflictError && err.detail.reason === 'no_fix') {
        const unreached = new Set((err.detail.review_issue_ids ?? []) as string[]);
        const rest = new Set([...fixes].filter((id) => !unreached.has(id)));
        setChanges({ fixes: rest });
        setNotice({
          color: 'orange',
          text: `Not saved: ${err.message}. That fix is dropped${
            edits.size + rest.size ? ' — the rest are kept; save again' : ''
          }.`,
        });
      } else if (err instanceof ApiConflictError && err.detail.reason === 'superseded') {
        setNotice({
          color: 'orange',
          text: 'A newer draft replaced this one first. Your changes move to it — check them and save again.',
        });
        await onChanged();
      } else {
        if (!(err instanceof ApiConflictError)) {
          Sentry.captureException(toError(err));
        }
        setNotice({ color: 'red', text: `Not saved: ${toError(err).message}` });
      }
    } finally {
      setSaving(false);
    }
  };

  const release = async () => {
    setReleasing(true);
    setNotice(null);
    try {
      const result = await vnextApiClient.releaseDraft(header.reportId, document.id);
      setConfirming(false);
      await onChanged();
      onReleased(released(result));
    } catch (err) {
      setConfirming(false);
      if (!(err instanceof ApiConflictError)) {
        Sentry.captureException(toError(err));
      }
      setNotice({ color: 'red', text: `Not released: ${toError(err).message}` });
    } finally {
      setReleasing(false);
    }
  };

  // The keys, on the selected issue — none while a paragraph is being
  // typed in, but ⌘↵ saves.
  const focused = open.find((i) => i.id === selectedIssue) ?? null;
  const step = (delta: 1 | -1) => {
    if (open.length === 0) {
      return;
    }
    const at = open.findIndex((i) => i.id === selectedIssue);
    const to =
      at === -1 ? (delta === 1 ? 0 : open.length - 1) : (at + delta + open.length) % open.length;
    select(open[to].id);
  };
  useWorkbenchKeys(
    {
      j: () => step(1),
      k: () => step(-1),
      '1': () => {
        if (focused && canEdit && focused.layoutFix) {
          toggleFix(focused);
        } else if (focused && canEdit) {
          fixYourself(focused);
        }
      },
      '2': () => focused && void resolveIssue(focused, 'accepted').catch(() => undefined),
      '3': () => focused && void resolveIssue(focused, 'dismissed').catch(() => undefined),
      '4': () => focused && void resolveIssue(focused, 'fixed').catch(() => undefined),
      'Mod+Enter': () => setSaveAsked(true),
    },
    draft && !confirming && pagesAt === null
  );

  useEffect(() => {
    if (saveAsked) {
      setSaveAsked(false);
      if (pending > 0 && !saving) {
        void save();
      }
    }
  }, [saveAsked]);

  // The way back to Needs you: on from the last issue closed here (the next
  // open one of this draft's), else at the one in focus. A fix leaves its
  // issue in focus, closed — and the new draft it's saved as opens afresh,
  // with nothing remembered — so an issue in focus that's closed is the last
  // one closed.
  const backTo = () => {
    const from =
      issues.find((i) => i.id === selectedIssue && i.status !== 'open')?.id ?? lastClosed;
    if (from) {
      const at = issues
        .filter((i) => i.status === 'open' || i.id === from)
        .sort(issueOrder)
        .findIndex((i) => i.id === from);
      const after = issues
        .filter((i) => i.status === 'open' || i.id === from)
        .sort(issueOrder)
        .slice(at + 1)
        .find((i) => i.status === 'open' && i.id !== from);
      return after?.id ?? null;
    }
    return focused?.id ?? null;
  };

  const renderIssue = (issue: ReviewIssue, place?: MarginPlace) => {
    const key = paraKey(issue);
    return (
      <IssueCard
        key={issue.id}
        issue={issue}
        selected={issue.id === selectedIssue}
        onSelect={(chosen) => select(chosen.id)}
        changed={key !== null && changed.has(key)}
        pendingEdit={key !== null && pendingParas.has(key)}
        canFix={canEdit}
        fixPending={fixes.has(issue.id)}
        onToggleFix={toggleFix}
        onFixYourself={canEdit && editableBlock(issue) ? fixYourself : undefined}
        onFixWithAssistant={
          onAskCopilot && canEdit
            ? (chosen) =>
                onAskCopilot({
                  document: 'draft',
                  documentId: document.id,
                  paraIds: chosen.paraId ? [chosen.paraId] : [],
                  quote: chosen.quote ?? '',
                  prompt: `Fix this review issue (${words(chosen.rule)}): ${chosen.message}`,
                })
            : undefined
        }
        onResolve={resolveIssue}
        onSeePage={
          draft ? (chosen) => setPagesAt({ page: chosen.page ?? 1, issueId: chosen.id }) : undefined
        }
        keys={draft}
        cardRef={(el) => {
          if (el) {
            cards.current.set(issue.id, el);
          } else {
            cards.current.delete(issue.id);
          }
          place?.cardRef(el);
        }}
        style={place?.style}
      />
    );
  };

  const body = (
    <Box ref={docRef} onClick={onDocClick}>
      {failed ? (
        <Text size="sm" c="red">
          {failed}
        </Text>
      ) : null}
      {!failed && !url ? (
        <Center h={120}>
          <Loader size="sm" />
        </Center>
      ) : null}
      {url ? (
        <DocxContent
          key={document.id}
          docxUrl={url}
          cacheKey={`${document.kind}:${document.id}:${document.workbenchKey ?? ''}`}
          edit={editing && canEdit ? binding : undefined}
          onRendered={(model) => {
            modelRef.current = model;
            setRendered((n) => n + 1);
          }}
          decorate={decorate}
        />
      ) : null}
    </Box>
  );

  return (
    <Grid
      ref={sizeRef}
      gutter="md"
      type="container"
      breakpoints={CONTAINER_BREAKPOINTS}
      data-testid="draft-view"
    >
      <Grid.Col span={{ base: 12, lg: margin ? 9 : 8 }}>
        <Stack gap="sm">
          <Group justify="space-between">
            <Group gap={6}>
              {draft ? (
                <Badge
                  size="sm"
                  variant="light"
                  color={document.status === 'built' ? 'blue' : 'gray'}
                >
                  {document.author ? 'Edited draft' : document.revision ? 'Revised draft' : 'Draft'}{' '}
                  · {words(document.status)}
                </Badge>
              ) : (
                <Badge size="sm" variant="light" color="gray">
                  Version {document.version}
                </Badge>
              )}
            </Group>
            {canEdit ? (
              <Group gap="xs">
                <Button
                  size="xs"
                  variant={editing ? 'filled' : 'default'}
                  onClick={() => {
                    setEditing(!editing);
                    setActive(null);
                  }}
                  data-testid="draft-edit-toggle"
                >
                  {editing ? 'Stop editing' : 'Edit text'}
                </Button>
                <Button
                  size="xs"
                  onClick={() => setConfirming(true)}
                  disabled={pending > 0}
                  title={pending > 0 ? 'Save or discard your changes first' : undefined}
                  data-testid="draft-release"
                >
                  Release…
                </Button>
              </Group>
            ) : null}
          </Group>
          {document.outdated ? (
            <Alert color="orange" variant="light" data-testid="draft-outdated">
              The case has moved on since this draft was written — its records or its checklist
              changed.{' '}
              {document.author
                ? "It isn't redrafted over a person's edits: ask the Assistant for a redraft when you want one (it replaces this draft, edits and all)."
                : 'A redraft follows once the case settles, or ask the Assistant for one.'}
            </Alert>
          ) : null}
          {notice ? (
            <Alert
              color={notice.color}
              variant="light"
              withCloseButton
              onClose={() => setNotice(null)}
            >
              {notice.text}
            </Alert>
          ) : null}
          {draft && (open.length > 0 || onBackToNeedsYou) ? (
            <DocumentIssueStrip
              open={open.length}
              needsYouTotal={needsYouTotal}
              onBack={onBackToNeedsYou ? () => onBackToNeedsYou(backTo()) : undefined}
              groups={groups}
              onResolveAll={canEdit ? resolveAll : undefined}
            />
          ) : null}
          {pending > 0 ? (
            <Card withBorder padding="xs" radius="sm" data-testid="draft-pending">
              <Group justify="space-between">
                <Text size="sm">{plural(pending, 'change')} not saved yet</Text>
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="subtle"
                    color="red"
                    disabled={saving}
                    onClick={() => {
                      onChangesChange(null);
                      setActive(null);
                    }}
                  >
                    Discard
                  </Button>
                  <Button
                    size="xs"
                    loading={saving}
                    onClick={() => void save()}
                    data-testid="draft-save"
                  >
                    Save as a new draft (⌘↵)
                  </Button>
                </Group>
              </Group>
            </Card>
          ) : null}
          {margin ? (
            <div className={classes.page}>
              {body}
              <ReviewMargin
                issues={shown}
                docRoot={docRef}
                selected={selectedIssue}
                renderIssue={renderIssue}
              />
            </div>
          ) : (
            body
          )}
        </Stack>
      </Grid.Col>
      <Grid.Col span={{ base: 12, lg: margin ? 3 : 4 }}>
        <Stack gap="sm">
          {draft && !margin ? (
            <ReviewIssues issues={shown} renderIssue={(issue) => renderIssue(issue)} />
          ) : null}
          <LayoutCheckPanel
            reportId={header.reportId}
            kind={draft ? 'draft' : 'version'}
            documentId={document.id}
          />
        </Stack>
      </Grid.Col>
      {draft && pagesAt ? (
        <PagesModal
          reportId={header.reportId}
          draftId={document.id}
          page={pagesAt.page}
          issueId={pagesAt.issueId}
          issues={open}
          onClose={() => setPagesAt(null)}
          onPickIssue={(issueId) => {
            setPagesAt(null);
            select(issueId);
          }}
        />
      ) : null}
      <Modal
        opened={confirming}
        onClose={() => setConfirming(false)}
        title="Release this draft as the report?"
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            It becomes the report&apos;s next version — what the client sees once it&apos;s
            approved.
            {['in_qa', 'completed'].includes(header.lifecycleStage)
              ? ''
              : ' The report completes: where approval is required, it goes to QA.'}
          </Text>
          {(document.review?.holdingErrors ?? 0) > 0 ? (
            <Text size="sm" c="red" data-testid="release-holding">
              {document.review?.holdingErrors} validator error(s) hold the release — fix, keep or
              dismiss them first.
            </Text>
          ) : null}
          {(document.review?.openErrors ?? 0) - (document.review?.holdingErrors ?? 0) > 0 ||
          (document.review?.openWarnings ?? 0) > 0 ? (
            <Text size="sm" c="dimmed" data-testid="release-carried">
              {(document.review?.openErrors ?? 0) -
                (document.review?.holdingErrors ?? 0) +
                (document.review?.openWarnings ?? 0)}{' '}
              open review issue(s) go with the draft to QA.
            </Text>
          ) : null}
          <Group justify="flex-end" gap="xs">
            <Button variant="default" size="xs" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button
              size="xs"
              loading={releasing}
              onClick={() => void release()}
              data-testid="draft-release-confirm"
            >
              Release
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Grid>
  );
}
